import { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { projectsApi, manpowerApi, rfisApi, usersApi } from '@/lib/apiClient';
import { invalidateHealthQueries, invalidateStartAlignmentQueries } from '@/lib/queryClient';
import { StateView, Badge } from '@/components';
import type { BadgeTone } from '@/components';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import { projectStage, type ProjectStage } from '@/lib/projectStage';
import ComingSoon from '@/components/ComingSoon';
import RfisTab from './RfisTab';
import SubmittalsTab from './SubmittalsTab';
import DailyLogsTab from './DailyLogsTab';
import ScheduleTab from './ScheduleTab';
import MobilizationTab from './MobilizationTab';
import EquipmentTab from './EquipmentTab';
import ReceivingTab from './ReceivingTab';
import MaterialsTab from './MaterialsTab';
import DocumentsTab from './DocumentsTab';
import PhotosTab from './PhotosTab';
import DrawingsTab from './DrawingsTab';
import ChangesTab from './ChangesTab';
import PunchTab from './punch/PunchTab';
import FinancialsTab from './FinancialsTab';
import CommitmentsTab from './CommitmentsTab';
import ContractsTab from '../contracts/ContractsTab';
import QaqcTab from './QaqcTab';
import SurveyTab from './SurveyTab';
import ManpowerTab from './ManpowerTab';
import ProjectReportsTab from './ProjectReportsTab';
import WeeklyReportTab from './weekly/WeeklyReportTab';
import ContactsTab from './contacts/ContactsTab';
import MeetingsTab from './meetings/MeetingsTab';
import ProjectNavRail from './ProjectNavRail';
import ProjectActivityCard from './ProjectActivityCard';
import OverallCompletionPanel from './schedule/OverallCompletionPanel';
import PhaseScheduleCard from './schedule/PhaseScheduleCard';
import PhaseScheduleDrawer from './schedule/PhaseScheduleDrawer';
import { isFieldLeaderOnly as isFieldLeaderOnlyFn } from '@/lib/access';
import { overviewStartMismatch } from '@/lib/startAlignment';
import { laborHoursState } from '@/lib/laborHours';
import {
  phaseKeysFor, isFabDivision, phaseStatus, PHASE_STATUS_LABEL, weightedOverall, laborStatus,
  mobEndDivergence, sameDivergenceAction, actualStartUnclaimed,
  type ProjectPhase, type PhaseKey,
} from '@/lib/schedule';
import { isConflictError } from '@/lib/httpErrors';
import {
  type OverviewProject,
  useCanEditProject,
  useCanCreateRfi,
  OverviewEditStyles,
  BasicsSection,
  TeamSection,
  ScheduleSection,
  NoticeToProceedSection,
  JobInfoSection,
  NotesSection,
  DangerZoneSection,
} from './OverviewEdit';
import { BidLinkSection } from './BidLinkSection';

// ─── Types (local — projectsApi.get returns `any`) ──────────────────────────
type Health = 'on-track' | 'at-risk' | 'off-track';

interface NamedRef { id: number | string; label?: string | null; displayName?: string | null }

// The workspace consumes the full GET /projects/:id record. OverviewProject
// (defined alongside the editable sections) already enumerates every field the
// header, KPIs, and inline-edit sections need.
type ProjectDetail = OverviewProject;

// ─── Tabs (mirrors the design's project workspace) ──────────────────────────
const TABS: { key: string; label: string }[] = [
  { key: 'overview',     label: 'Overview' },
  { key: 'weekly',       label: 'Weekly Report' },
  { key: 'contacts',     label: 'Contacts' },
  { key: 'daily',        label: 'Daily Logs' },
  { key: 'mobilization', label: 'Mobilization' },
  { key: 'schedule',     label: 'Schedule' },
  { key: 'manpower',     label: 'Manpower' },
  { key: 'equipment',    label: 'Equipment' },
  { key: 'survey',       label: 'Survey' },
  { key: 'receiving',    label: 'Receiving' },
  { key: 'materials',    label: 'Materials' },
  { key: 'documents',    label: 'Documents' },
  // Agendas and minutes, beside Documents where the request put them (#644).
  { key: 'meetings',     label: 'Meetings' },
  // The site photo feed, a second way in beside Daily Logs ▸ Photos (#665).
  { key: 'photos',       label: 'Photos' },
  { key: 'drawings',     label: 'Drawings' },
  { key: 'changes',      label: 'Change Mgmt' },
  { key: 'qaqc',         label: 'QA / QC' },
  { key: 'punch',        label: 'Punch List' },
  { key: 'rfis',         label: 'RFIs' },
  { key: 'submittals',   label: 'Submittals' },
  { key: 'safety',       label: 'Safety' },
  { key: 'financials',   label: 'Financials' },
  { key: 'commitment',   label: 'Commitments' },
  { key: 'contracts',    label: 'Contracts' },
  { key: 'reports',      label: 'Reports' },
];

/** Which signal decided a job's health. Mirrors HealthRule on the API. */
type HealthRule =
  | 'closed' | 'past-completion' | 'mob-delayed' | 'mob-off-track'
  | 'start-slipped' | 'clear' | 'no-dates';

/**
 * What each health value means, in the order the API checks them.
 *
 * PMs asked what "At Risk" meant (product owner, 2026-07-28). The important thing this
 * has to convey — and the thing nobody could have guessed from a coloured dot —
 * is that health is CALCULATED. Nobody sets it, so there is nobody to ask, and
 * "fixing" it means fixing the underlying dates or mobilization status.
 */
const HEALTH_RULES: { tone: Health; when: string }[] = [
  { tone: 'off-track', when: 'The job is past its target completion date and isn’t marked complete.' },
  { tone: 'off-track', when: 'Mobilization status is Delayed.' },
  { tone: 'at-risk',   when: 'Mobilization status is Off Track.' },
  { tone: 'at-risk',   when: 'The job is in Construction and its current start date moved more than 14 days later than the original start.' },
  { tone: 'on-track',  when: 'None of the above — or the job is complete or cancelled.' },
];

const HEALTH: Record<Health, { label: string; dot: string; tone: BadgeTone }> = {
  'on-track':  { label: 'On Track',  dot: 'var(--ew-green)',  tone: 'success' },
  'at-risk':   { label: 'At Risk',   dot: 'var(--ew-safety)', tone: 'warning' },
  'off-track': { label: 'Off Track', dot: 'var(--ew-danger)', tone: 'danger'  },
};

function money(v: number | null | undefined): string {
  if (v == null) return '—';
  return '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });
}
// fmtDate comes from lib/dates (parseLocalDate-based): the KPI fields it
// renders (start / target completion) are calendar dates, and naive
// new Date() parsing shifted them back a day in Eastern time.
function personName(p: NamedRef | null): string {
  return p?.displayName ?? p?.label ?? '—';
}

const css = `
  .pw-scroll { max-width: 1440px; margin: 0 auto; padding: var(--ew-s-5) var(--ew-s-6) var(--ew-s-8); }
  /* Desktop nav = the left rail; the top tab bar is mobile-only. One media
     query swaps them — both share setTab + tabCounts, so behavior is identical. */
  .pw-tabbar { display: none; }
  @media (max-width: 768px) {
    .pw-tabbar { display: flex; }
    .pw-rail { display: none; }
  }
  .pw-tab {
    background: transparent; border: 0; border-bottom: 2px solid transparent;
    padding: 12px var(--ew-s-4); margin-bottom: -1px; cursor: pointer; white-space: nowrap;
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font); color: var(--ew-n-500);
    transition: color var(--ew-dur-fast) var(--ew-ease);
  }
  .pw-tab:hover { color: var(--ew-dark); }
  .pw-tab.active { color: var(--ew-dark); border-bottom-color: var(--ew-green); }
  .pw-tab-count {
    margin-left: 6px; padding: 1px 7px; border-radius: var(--ew-r-pill);
    background: var(--ew-n-100); color: var(--ew-n-600);
    font: var(--ew-fw-semibold) 11px/1.6 var(--ew-font); font-variant-numeric: tabular-nums;
  }
  /* Stage lock banner — SYSTAC (edit-locked) / ClosedWon (activity-locked).
     Amber warning strip pinned above the tab body on every tab. */
  .pw-lock {
    display: flex; align-items: flex-start; gap: var(--ew-s-3);
    background: var(--ew-safety-50, #fff8e6); border: 1px solid var(--ew-safety, #e0a400);
    border-radius: var(--ew-r-md); padding: 12px var(--ew-s-4); margin-bottom: var(--ew-s-4);
  }
  .pw-lock-badge {
    flex: none; font: var(--ew-fw-semibold) 11px/1.6 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: #8a6400; background: var(--ew-safety-100, #ffefc2);
    border-radius: var(--ew-r-pill); padding: 2px 10px; white-space: nowrap;
  }
  .pw-lock-text { font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font); color: var(--ew-n-700); }
  .pw-card { background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); }
  .pw-card-hd {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); margin: 0; padding: 14px var(--ew-s-4);
    border-bottom: 1px solid var(--ew-n-100); display: flex; align-items: center;
  }
  .pw-kpi {
    background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
    padding: var(--ew-s-4); position: relative; overflow: hidden;
    display: flex; flex-direction: column; gap: var(--ew-s-1);
  }
  .pw-kpi-strip { position: absolute; left: 0; top: 0; bottom: 0; width: 3px; }
  .pw-kpi-label { font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); }
  .pw-kpi-value { font: var(--ew-fw-semibold) 22px/1.1 var(--ew-font); font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
  .pw-detail-row { display: flex; justify-content: space-between; gap: var(--ew-s-4); padding: 10px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .pw-detail-row span:first-child { color: var(--ew-n-500); }
  .pw-detail-row span:last-child { color: var(--ew-dark); font-weight: var(--ew-fw-medium); text-align: right; }

  /* Two-column command-center layout (content + sidebar). Collapses to a
     single stacked column on tablet-portrait and phones; the sidebar
     (RFI/Safety summaries, RFI Details, etc.) drops below the main column. */
  .pw-two-col { display: grid; gap: var(--ew-s-4); grid-template-columns: 2fr 1fr; align-items: start; }
  @media (max-width: 900px) {
    .pw-two-col { grid-template-columns: 1fr; }
  }
  @media (max-width: 768px) {
    .pw-scroll { padding: var(--ew-s-4) var(--ew-s-4) var(--ew-s-6); }
    .pw-header { padding: 16px var(--ew-s-4) 0 !important; }
    .pw-header h1 { font-size: 22px !important; }
    /* Header actions go full-width below the title instead of crowding it. */
    .pw-actions { width: 100%; }
    .pw-actions .btn { flex: 1; justify-content: center; }
  }
`;

// Amber lock strip shown above the tab body on every tab of a placeholder-job
// project. SYSTAC → edit-locked; ClosedWon → activity-locked (message picked in
// projectStage). Purely informational — the disabled controls do the gating.
// No manual "award" action here: SYSTAC → ClosedWon is derived from Bid Tracker
// (external), and ClosedWon → a real Sage # is the normal Basics renumber
// (ClosedWon isn't edit-locked).
function StageLockBanner({ stage }: { stage: ProjectStage }) {
  return (
    <div className="pw-lock" role="status">
      <span className="pw-lock-badge">{stage.editLocked ? 'Locked · SYSTAC' : 'Locked · No Sage #'}</span>
      <span className="pw-lock-text">{stage.reason}</span>
    </div>
  );
}

export default function ProjectWorkspace() {
  const id = Number(useParams().id);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'overview';
  /** Open a tab. `extra` lands a sub-view or record too — e.g. { log: '12' }
   *  opens that daily log on the Daily Logs tab. */
  const setTab = (key: string, extra?: Record<string, string>) =>
    setParams(key === 'overview' ? {} : { tab: key, ...extra }, { replace: true });
  /** Open the RFIs tab WITH the create form up — see `?rfi=new` in RfisTab. */
  const openNewRfi = () => setParams({ tab: 'rfis', rfi: 'new' }, { replace: true });

  const query = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsApi.get(id) as Promise<ProjectDetail>,
    enabled: Number.isFinite(id),
  });

  // Current user — drives field-leader tab gating (below) and cost access.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60_000 });
  const me = meQuery.data;
  // Shared with App.tsx and ChangesTab — see lib/access. reports.view is
  // deliberately not counted as office, matching isFieldLeaderScoped on the API.
  const isFieldLeaderOnly = isFieldLeaderOnlyFn(me);
  // Field leaders hold rfis.create, so this is wider than canEdit by design.
  const canCreateRfi = useCanCreateRfi();


  // Per-tab open-item counts for the tab badges. Intentionally non-blocking:
  // the project + tabs render immediately and the pills pop in when this
  // resolves. Never gates the page; failures just mean no pills (retry:false).
  const countsQuery = useQuery({
    queryKey: ['project-counts', id],
    queryFn: () => projectsApi.counts(id),
    enabled: Number.isFinite(id),
    staleTime: 60_000,
    retry: false,
  });

  // Financials tab gating — the whole surface is behind financials.view AND the
  // showProjectCosts admin toggle (same toggle the Cost tab uses).
  const configQuery = useQuery({ queryKey: ['manpower', 'config'], queryFn: manpowerApi.config, staleTime: 60_000, enabled: Number.isFinite(id) });
  const showCosts = configQuery.data?.showProjectCosts === '1';
  const canViewFinancials = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('financials.view') ?? false);

  // Map each count to its tab key (tabs without a count concept are absent).
  const counts = countsQuery.data;
  const tabCounts: Record<string, number | undefined> = {
    mobilization: counts?.mobilization,
    punch:        counts?.punch,
    equipment:    counts?.equipment,
    receiving:    counts?.receiving,
    materials:    counts?.materials,
    qaqc:         counts?.qaqc,
    changes:      counts?.changes,
    rfis:         counts?.rfis,
    submittals:   counts?.submittals,
  };

  // Tab visibility — hide the office roll-up tabs (Cost / Safety / Reports) from
  // field leaders (me / isFieldLeaderOnly computed above). Note: only Cost is
  // ALSO blocked server-side; Safety/Reports are UX-hidden here.
  const OFFICE_TABS = new Set(['commitment', 'contracts', 'safety', 'reports']);
  const hiddenTabs = new Set<string>(isFieldLeaderOnly ? OFFICE_TABS : []);
  // Financials + Commitments share the financials.view gate + the cost toggle.
  if (!canViewFinancials || !showCosts) { hiddenTabs.add('financials'); hiddenTabs.add('commitment'); }
  // reports.view opens the Reports SURFACE (this tab and the top-level Reports
  // page alike); the per-report permissions then gate each report on it. A role
  // whose reports.view was revoked must not see the tab even if it still carries
  // a report grant — same rule as App.tsx's canViewReports (review, #352). The
  // report DATA stays safe either way: each endpoint checks its own permission.
  const canViewReports = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('reports.view') ?? false);
  if (!canViewReports) hiddenTabs.add('reports');
  // Contracts opens on contracts.view or contracts.edit (which also reads), the same
  // rule as the top-level Contracts tab in App.tsx. The API checks it either way.
  const canViewContracts = (me?.roles.includes('Admin') ?? false)
    || (me?.permissions.includes('contracts.view') ?? false)
    || (me?.permissions.includes('contracts.edit') ?? false);
  if (!canViewContracts) hiddenTabs.add('contracts');
  const visibleTabs = TABS.filter((t) => !hiddenTabs.has(t.key));

  // Never render a hidden tab's body (e.g. a deep link to ?tab=cost) — fall back
  // to Overview so the gating can't be bypassed via the URL.
  const viewTab = visibleTabs.some((t) => t.key === tab) ? tab : 'overview';
  const activeLabel = visibleTabs.find((t) => t.key === viewTab)?.label ?? 'Overview';

  // Left-rail collapse — click-pinned and persisted, so the scheduler's
  // preference survives reloads (handoff: pinned, not hover-reveal).
  const [railCollapsed, setRailCollapsed] = useState(
    () => localStorage.getItem('pmt.navRailCollapsed') === '1',
  );
  const toggleRail = () => setRailCollapsed((v) => {
    localStorage.setItem('pmt.navRailCollapsed', v ? '0' : '1');
    return !v;
  });

  return (
    <>
      <style>{css}</style>
      <StateView query={query} isEmpty={(d) => !d} empty="Project not found.">
        {(p) => {
          const h = HEALTH[p.health] ?? HEALTH['on-track'];
          // Job-number lifecycle locks: SYSTAC = edit-locked (read-only),
          // ClosedWon = activity-locked (no requests until a Sage #). Drives the
          // banner + disables edit/request controls; the API enforces it too.
          const stage = projectStage(p.jobNumber, p.bidClosedWonAt);
          // Contract source precedence: Intacct revised total (synced) → the
          // manual contract value. No third source: the Cost Tracker fallback
          // went with that app (product owner, 2026-08-04). A job with no Intacct
          // contract shows nothing and raises ContractSourcePrompt instead.
          // ⚠️ Field leaders never see a contract figure (product owner, 2026-08-03 —
          // the executive team doesn't want it reaching the field). The API
          // already nulls all three contract fields for them, so this is only
          // about not rendering an empty "Contract —" where money used to be;
          // the gate itself is server-side, in projects/lib/contract-visibility.
          // Intacct is the source (product owner, 2026-08-04). The Cost Tracker fallback is
          // gone with that app; `contractValue` stays as a display fallback but no
          // live job uses it and there is no editor input for it.
          const effectiveContract = isFieldLeaderOnly ? null : (p.sageContract?.revised ?? p.contractValue ?? null);
          return (
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              {/* ── Left rail (desktop) — hidden ≤768px where the top tab bar
                  takes over (preserves the shipped mobile behavior). ── */}
              <ProjectNavRail
                jobNumber={p.jobNumber}
                activeTab={viewTab}
                counts={tabCounts}
                onSelect={setTab}
                collapsed={railCollapsed}
                onToggle={toggleRail}
                hiddenKeys={hiddenTabs.size ? hiddenTabs : undefined}
              />

              <div style={{ flex: 1, minWidth: 0 }}>
              {/* ── Header ── */}
              <section className="pw-header" style={{ background: '#fff', borderBottom: '1px solid var(--ew-n-200)', padding: '22px var(--ew-s-6) var(--ew-s-4)' }}>
                <div style={{ maxWidth: 1440, margin: '0 auto' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--ew-s-3)', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ font: 'var(--ew-fw-medium) var(--ew-fs-meta)/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-400)' }}>
                        <a onClick={() => navigate('/projects')} style={{ color: 'var(--ew-n-500)', cursor: 'pointer' }}>Projects</a>
                        {p.division?.label ? <> &nbsp;/&nbsp; <span style={{ color: 'var(--ew-n-500)' }}>{p.division.label}</span></> : null}
                        &nbsp;/&nbsp; <span style={{ color: 'var(--ew-dark)', fontWeight: 'var(--ew-fw-medium)' as unknown as number }}>{p.jobNumber}</span>
                      </div>
                      <h1 style={{ font: 'var(--ew-fw-semibold) 30px/1.1 var(--ew-font)', letterSpacing: '-0.01em', margin: '6px 0' }}>{p.title}</h1>
                      <div style={{ display: 'flex', gap: 'var(--ew-s-4)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm)/1 var(--ew-font)', color: 'var(--ew-n-500)', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span><b style={{ color: 'var(--ew-dark)', fontWeight: 500 }}>Job #</b> {p.jobNumber}</span>
                        <span><b style={{ color: 'var(--ew-dark)', fontWeight: 500 }}>Owner</b> {p.client ?? '—'}</span>
                        {/* Omitted entirely for field leaders — "Contract —" would
                            advertise a figure they're not meant to know exists. */}
                        {!isFieldLeaderOnly && (
                          <span><b style={{ color: 'var(--ew-dark)', fontWeight: 500 }}>Contract</b> {money(effectiveContract)}</span>
                        )}
                        <span><b style={{ color: 'var(--ew-dark)', fontWeight: 500 }}>PM</b> {personName(p.primaryPm)}</span>
                        <span><b style={{ color: 'var(--ew-dark)', fontWeight: 500 }}>Field Leader</b> {p.fieldLeaders && p.fieldLeaders.length > 0 ? p.fieldLeaders.map((f) => f.displayName ?? f.email).join(', ') : '—'}</span>
                        {/* The header badge is where people first meet this, so it
                            carries the reason on hover; the Overview explainer has
                            the full rule set. */}
                        <span
                          title={p.healthReason ? `${h.label} — ${p.healthReason}` : h.label}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, color: h.dot, cursor: 'help' }}
                        >
                          <span style={{ width: 8, height: 8, borderRadius: 999, background: h.dot }} />{h.label}
                        </span>
                      </div>
                    </div>
                    {/* Quick-create shortcuts — hidden while activity-locked
                        (SYSTAC / ClosedWon), since both lead to blocked flows. */}
                    {stage.activityLocked ? null : (
                      <div className="pw-actions" style={{ display: 'flex', gap: 'var(--ew-s-2)' }}>
                        <button className="btn btn-secondary" onClick={() => setTab('daily')}>Daily Log</button>
                        {/* ⚠️ This used to call setTab('rfis') and stop there —
                            landing you on the list, where for a field leader the
                            create button wasn't even rendered. Hence "the button
                            is there but it doesn't do anything" (product owner,
                            2026-08-04). It now asks the tab for the FORM, and is
                            hidden from anyone who can't submit one. */}
                        {canCreateRfi && (
                          <button className="btn btn-primary" onClick={openNewRfi}>+ New RFI</button>
                        )}
                      </div>
                    )}
                  </div>
                  {/* ── Tabs (mobile only — the rail owns desktop nav) ── */}
                  <div className="pw-tabbar" style={{ gap: 0, marginTop: 'var(--ew-s-5)', overflowX: 'auto' }}>
                    {visibleTabs.map((t) => {
                      const n = tabCounts[t.key];
                      return (
                        <button
                          key={t.key}
                          className={`pw-tab${t.key === viewTab ? ' active' : ''}`}
                          onClick={() => setTab(t.key)}
                        >
                          {t.label}
                          {typeof n === 'number' && n > 0 ? <span className="pw-tab-count">{n}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              {/* ── Body ── */}
              <main className="pw-scroll">
                {stage.reason ? <StageLockBanner stage={stage} /> : null}
                {/*
                  ⚠️ key={p.id} is REQUIRED, not decoration. Navigating straight
                  from one project to another keeps the same route, so React
                  Router reuses this tree and reconciles Overview in the same
                  position — its local state survives into a different job.
                  Overview holds three pieces of per-project state and all three
                  leak without this: the "wrote nothing" notice would appear on a
                  job where nothing was clicked, an open schedule drawer would
                  carry over, and an in-flight accept would leave the next job's
                  button stuck on "Updating…". Keying discards the instance
                  instead of patching each field, so the next piece of state
                  added here is safe by default. Queries are cached separately,
                  so remounting costs no extra fetches.
                */}
                {viewTab === 'overview' ? <Overview key={p.id} project={p} contractValue={effectiveContract} hideContract={isFieldLeaderOnly} onOpenTab={setTab} editLocked={stage.editLocked} />
                  /* key={p.id}: the export window's ticked days must never carry from one job to the next. */
                  : viewTab === 'weekly' ? <WeeklyReportTab key={p.id} projectId={p.id} />
                  : viewTab === 'contacts' ? <ContactsTab key={p.id} projectId={p.id} />
                  : viewTab === 'financials' ? <FinancialsTab projectId={p.id} />
                  /* key={p.id}: the tab holds drafts (the buyout plan, the month-end accruals) that must never carry from one job to the next. */
                  : viewTab === 'commitment' ? <CommitmentsTab key={p.id} projectId={p.id} />
                  /* key={p.id}: the tab holds open dialogs whose contents must never carry from one job to the next. */
                  : viewTab === 'contracts' ? <ContractsTab key={p.id} projectId={p.id} jobLabel={`${p.jobNumber} · ${p.title}`} />
                  : viewTab === 'safety' ? <SafetyTab projectId={p.id} />
                  : viewTab === 'rfis' ? <RfisTab projectId={p.id} locked={stage.activityLocked} />
                  : viewTab === 'submittals' ? <SubmittalsTab projectId={p.id} locked={stage.activityLocked} />
                  : viewTab === 'daily' ? <DailyLogsTab projectId={p.id} locked={stage.activityLocked} />
                  : viewTab === 'schedule' ? <ScheduleTab projectId={p.id} />
                  : viewTab === 'mobilization' ? <MobilizationTab projectId={p.id} locked={stage.editLocked} />
                  : viewTab === 'equipment' ? <EquipmentTab projectId={p.id} locked={stage.activityLocked} />
                  : viewTab === 'receiving' ? <ReceivingTab projectId={p.id} locked={stage.activityLocked} />
                  : viewTab === 'materials' ? <MaterialsTab projectId={p.id} locked={stage.activityLocked} />
                  : viewTab === 'documents' ? <DocumentsTab projectId={p.id} />
                  /* key={p.id}: an open meeting's unsaved edits must never carry from one job to the next. */
                  : viewTab === 'meetings' ? <MeetingsTab key={p.id} projectId={p.id} />
                  /* key={p.id}: the feed's search and filters must never carry from one job to the next. */
                  : viewTab === 'photos' ? <PhotosTab key={p.id} projectId={p.id} locked={stage.activityLocked} onOpenLog={(logId) => setTab('daily', { log: String(logId) })} />
                  : viewTab === 'drawings' ? <DrawingsTab projectId={p.id} />
                  : viewTab === 'changes' ? <ChangesTab projectId={p.id} locked={stage.activityLocked} />
                  /* key={p.id} for the same reason as Overview's: the tab holds
                     open dialogs (settings, publish, add item) whose choices
                     must never carry from one job to the next (Codex on #565). */
                  : viewTab === 'punch' ? <PunchTab key={p.id} projectId={p.id} locked={stage.activityLocked} />
                  : viewTab === 'qaqc' ? <QaqcTab projectId={p.id} />
                  : viewTab === 'survey' ? <SurveyTab projectId={p.id} />
                  : viewTab === 'manpower' ? <ManpowerTab projectId={p.id} />
                  : viewTab === 'reports' ? <ProjectReportsTab projectId={p.id} />
                  : <ComingSoon title={activeLabel} note={`The ${activeLabel} workspace for this job is coming in a later phase.`} />}
              </main>
              </div>
            </div>
          );
        }}
      </StateView>
    </>
  );
}

// The old Cost tab (read-only Cost-Tracker mirror) was replaced by the native
// Commitments tab (modules/projects/CommitmentsTab) — budget/committed/spent +
// buyout planning on the Sage feeds. Contract/revenue/billing live on the
// Financials tab. There is no Costs tab and no Cost Tracker call: that app is
// being retired and the Intacct API feed supersedes it.

// ─── Safety (Phase 2: read-only mirror of E&W Safety / safety-site) ─────────
// One event in the Safety "Recent activity" feed (read-only mirror of safety-site).
type SafetyActivityType = 'Good Catch' | 'Site Inspection' | 'Equipment Inspection';
type RecentActivityItem = {
  id: number;
  type: SafetyActivityType;
  person: string | null;
  date: string | null;
};

// Per-type badge presentation. Good Catch → success (green); Site Inspection →
// info (the quality/blue tint); Equipment Inspection → the performance color
// (no shared Badge tone for it, so override via style — matches the KPI tile
// strips above).
const ACTIVITY_BADGE: Record<SafetyActivityType, { tone: BadgeTone; style?: React.CSSProperties }> = {
  'Good Catch':          { tone: 'success' },
  'Site Inspection':     { tone: 'info' },
  'Equipment Inspection':{ tone: 'neutral', style: { background: 'var(--ew-performance-50)', color: 'var(--ew-performance)' } },
};

function SafetyTab({ projectId }: { projectId: number }) {
  type SafetyData = {
    available: boolean;
    goodCatch: { total: number; people: { firstName: string | null; lastName: string | null; count: number }[] };
    inspections: { total: number; configured: boolean };
    equipmentInspections: { total: number; configured: boolean };
    recentActivity: RecentActivityItem[];
  };
  const q = useQuery<SafetyData>({
    queryKey: ['project-safety', projectId],
    queryFn: () => projectsApi.safety(projectId) as Promise<SafetyData>,
    staleTime: 60_000,
  });

  const banner = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-3)', background: '#fff', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '10px var(--ew-s-4)', marginBottom: 'var(--ew-s-4)' }}>
      <span style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-quality)' }}>◇ Synced from E&amp;W Safety</span>
      <span style={{ font: 'var(--ew-fw-regular) 12px var(--ew-font)', color: 'var(--ew-n-500)' }}>Read-only mirror</span>
    </div>
  );

  if (q.isLoading) return <>{banner}<ComingSoon title="Safety" note="Loading safety data…" /></>;
  if (q.isError || !q.data?.available) return <>{banner}<ComingSoon title="Safety" note="Couldn't reach E&W Safety for this job (check db_datareader on safety-site)." /></>;

  const d = q.data;
  const tiles: { label: string; value: string | number; strip: string; sub?: string }[] = [
    { label: 'Good Catches', value: d.goodCatch.total, strip: 'var(--ew-green)' },
    { label: 'Site Safety Inspections', value: d.inspections.configured ? d.inspections.total : '—', strip: 'var(--ew-quality)' },
    { label: 'Equipment Inspections', value: d.equipmentInspections.configured ? d.equipmentInspections.total : '—', strip: 'var(--ew-performance)' },
    { label: 'TPP', value: '—', strip: 'var(--ew-n-300)', sub: 'coming soon' },
  ];

  return (
    <>
      {banner}
      <div style={{ display: 'grid', gap: 'var(--ew-s-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {tiles.map((t) => (
          <div key={t.label} className="pw-kpi">
            <span className="pw-kpi-strip" style={{ background: t.strip }} />
            <span className="pw-kpi-label">{t.label}</span>
            <span className="pw-kpi-value">{t.value}</span>
            {t.sub ? <span style={{ font: 'var(--ew-fw-medium) 12px/1.2 var(--ew-font)', color: 'var(--ew-n-500)' }}>{t.sub}</span> : null}
          </div>
        ))}
      </div>
      {d.goodCatch.people.length > 0 ? (
        <div className="pw-card" style={{ marginTop: 'var(--ew-s-4)' }}>
          <h2 className="pw-card-hd">Good Catch leaders</h2>
          {d.goodCatch.people.slice(0, 8).map((person, idx) => (
            <div key={idx} className="pw-detail-row">
              <span>{[person.firstName, person.lastName].filter(Boolean).join(' ') || 'Unknown'}</span>
              <span>{person.count}</span>
            </div>
          ))}
        </div>
      ) : null}

      {/* Recent activity — newest-first feed across the three tracked safety
          form categories. Read-only mirror of safety-site. */}
      <div className="pw-card" style={{ marginTop: 'var(--ew-s-4)' }}>
        <h2 className="pw-card-hd">Recent activity</h2>
        {d.recentActivity.length === 0 ? (
          <p style={{ padding: 'var(--ew-s-4)', color: 'var(--ew-n-400)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', textAlign: 'center' }}>
            No recent safety activity.
          </p>
        ) : (
          d.recentActivity.map((ev) => {
            const cfg = ACTIVITY_BADGE[ev.type];
            return (
              <div key={ev.id} className="pw-detail-row" style={{ alignItems: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--ew-s-2)' }}>
                  <Badge label={ev.type} tone={cfg.tone} style={cfg.style} />
                  <span style={{ color: 'var(--ew-dark)' }}>{ev.person ?? '—'}</span>
                </span>
                {ev.date ? <span>{fmtDateTime(ev.date)}</span> : <span />}
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

// Seed a phase from the legacy project-level dates so existing projects absorb
// their schedule into the card without re-entry. Non-fab 'project' gets the full
// start/end; fab phases get only the outer bounds the legacy fields carry (the
// fab→install split isn't in the legacy data, so the user sets it once).
//
// ⚠️ DISPLAY ONLY since #383 — this no longer builds a write payload. The server
// applies the same table when it CREATES a phase row (`PHASE_LEGACY_SEED` in
// apps/api/src/projects/phase-legacy-seed.ts), so callers send only the fields
// they mean and nobody predicts INSERT-vs-UPDATE from a snapshot.
//
// ⚠️ IT IS A MIRROR OF THAT TABLE and the two must agree — this is what the card
// SHOWS, that is what a save STORES, so a drift means the row written is not the
// row the user was looking at. Change both, the way deriveScheduleHealth is.
// It could not simply move server-side: the projects list and the Manpower grid
// read the same `phases` array raw, and seeding it in the API would start
// rendering "Fab <start> → —" on every fab job that has never saved a schedule.
//
// ⚠️ `currentStartDate` seeds plannedStart, NOT actualStart (#380). THIS LINE WAS
// THE ORIGINAL DEFECT. `current_start_date` means "the start we are working to",
// and putting it in actualStart — the only slot that existed — made `phaseStatus`
// read every seeded job as In Progress, including jobs whose start had not
// arrived. It also labelled a plan "Actual Start" on the card, which is why job
// 10965's PM cleared it: correctly, since work had not begun, and that left the
// 2026-09-07 intent living only in the project column (#394).
function seedPhaseFromLegacy(phase: PhaseKey, p: ProjectDetail): ProjectPhase {
  const blank: ProjectPhase = {
    phase, originalStart: null, plannedStart: null, originalEnd: null,
    actualStart: null, actualStartSource: null, actualEnd: null,
    isProjectedEnd: true, completionPct: null, budgetLaborHours: null,
  };
  if (phase === 'project') return { ...blank, originalStart: p.originalStartDate, plannedStart: p.currentStartDate, originalEnd: p.estimatedCompletion, actualEnd: p.estimatedCompletion };
  if (phase === 'fabrication') return { ...blank, originalStart: p.originalStartDate };
  if (phase === 'installation') return { ...blank, originalEnd: p.estimatedCompletion, actualEnd: p.estimatedCompletion };
  return blank;
}

/**
 * "Crew schedule updated" notice on the project Overview.
 *
 * PM request relayed 2026-07-28: a PM wants to know their job's scheduled
 * manpower moved without having to go and compare the Manpower grid against
 * their memory of it. Reads the publish events the manpower service records
 * against the project, so it reports discrete changes with the span they
 * covered — not just "something is different now".
 *
 * Renders nothing at all when the job has never had a schedule published, so a
 * job that isn't on the manpower grid doesn't carry a dead panel.
 *
 * The link through was held back when this first shipped: the Manpower tab was
 * actuals-only (Paycor hours worked) and said nothing about the SCHEDULE, so a
 * "your schedule changed" notice pointing at it landed you somewhere that
 * couldn't answer the question. Restored now that the tab leads with a
 * Scheduled Crew summary.
 */
/**
 * "Why is this job At Risk?" — the reason it fired, plus the full rule set.
 *
 * Collapsed by default so an on-track job stays quiet, but the toggle is always
 * present because the question "what do these even mean" is just as common on a
 * green job as an amber one.
 */
function HealthExplainer({
  health, rule, reason,
}: { health: Health; rule: HealthRule | null; reason: string | null }) {
  const [open, setOpen] = useState(false);
  const h = HEALTH[health] ?? HEALTH['on-track'];

  return (
    <div style={{ marginBottom: 'var(--ew-s-4)' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, background: 'none',
          border: 'none', padding: 0, cursor: 'pointer',
          font: 'var(--ew-fw-medium) 12.5px/1.4 var(--ew-font)', color: 'var(--ew-n-600)',
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: h.dot, flex: 'none' }} />
        <span><b style={{ color: 'var(--ew-dark)' }}>{h.label}</b>{reason ? ` — ${reason}` : ''}</span>
        <span style={{ color: 'var(--ew-quality)', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
          {open ? 'Hide' : 'How is this set?'}
        </span>
      </button>

      {open && (
        <div style={{
          marginTop: 8, background: 'var(--ew-n-50)', border: '1px solid var(--ew-n-200)',
          borderRadius: 'var(--ew-r-md)', padding: '12px 14px',
          font: 'var(--ew-fw-regular) 12.5px/1.55 var(--ew-font)', color: 'var(--ew-n-700)',
        }}>
          <p style={{ margin: '0 0 8px' }}>
            <b>Health is calculated, not entered.</b> Nobody sets it by hand — the tracker works it
            out from the job’s status, mobilization status and dates. To change it, change those.
          </p>
          <div style={{ display: 'grid', gap: 6, marginBottom: 8 }}>
            {HEALTH_RULES.map((r, i) => {
              const t = HEALTH[r.tone];
              return (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <span style={{
                    flex: 'none', minWidth: 74, font: 'var(--ew-fw-semibold) 11px/1.5 var(--ew-font)',
                    color: t.dot === 'var(--ew-green)' ? 'var(--ew-green-700)' : t.dot,
                  }}>
                    {t.label}
                  </span>
                  <span>{r.when}</span>
                </div>
              );
            })}
          </div>
          <p style={{ margin: 0, color: 'var(--ew-n-500)' }}>
            Checked in that order — the first match wins, so a job past its completion date reports
            that rather than a slipped start. Dates are judged against the <b>later</b> of the target
            completion and the latest mobilization end, so an approved re-mob doesn’t read as overdue.
            {rule === 'no-dates' && ' This job has no target completion, so the date checks can’t run at all.'}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * "This job is in Construction but mobilization doesn't say Mobilized."
 *
 * A PROMPT, never an automatic change (product owner, 2026-07-28). Moving to Construction
 * could have stamped mobilization as Mobilized, but a job can genuinely be in
 * Construction while mobilization still lags — crew not on site, materials late —
 * and auto-stamping would erase exactly the signal worth seeing. So it asks.
 *
 * Deliberately NOT a health signal. Health is derived from dates and the
 * mobilization value itself; feeding a data-completeness nudge into it would put
 * jobs back on amber for a reason that isn't about the job. Once MOBILIZED is
 * genuinely maintained, the health rule that was removed in #257 ("finishing
 * within 30 days and not mobilized") becomes viable again — but not before.
 */
function MobilizationPrompt({ project: p }: { project: ProjectDetail }) {
  const status = (p.projectStatus?.label ?? '').trim().toLowerCase();
  const mob = (p.mobilizationStatus?.label ?? '').trim().toLowerCase();
  // Only for jobs actually under way. A Preconstruction job not being mobilized
  // is the expected state, not something to nag about.
  if (status !== 'construction') return null;
  // Nothing to say once it's Mobilized, and nothing useful to add when
  // mobilization is already flagged Delayed or Off Track — that's a live problem
  // being tracked, not a missing value.
  if (mob.includes('mobilized') || mob.includes('delay') || mob.includes('off')) return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 9, flexWrap: 'wrap',
      background: '#FFFDF5', border: '1px solid #E7D9A6', borderRadius: 'var(--ew-r-md)',
      padding: '9px 12px', marginBottom: 'var(--ew-s-4)', fontSize: 12.5, lineHeight: 1.5,
      color: '#6B5D1F',
    }}>
      <span style={{ fontWeight: 700 }}>Mobilization status check</span>
      <span>
        This job is in Construction, but mobilization still says
        {' '}<b>{p.mobilizationStatus?.label ?? 'nothing'}</b>. If the crew is on site, set it to
        {' '}<b>Mobilized</b> in Basics below — it’s how the tracker knows mobilization finished
        rather than merely being on schedule.
      </span>
    </div>
  );
}

/**
 * "This job has no estimated labor hours."
 *
 * Product owner, 2026-08-04: the estimator types the hours at job set-up and the job is
 * flagged until they do. The figure used to be autocalculated by Intacct and
 * routinely ran low, so the app showed a number nobody stood behind; now it is
 * either entered or visibly absent.
 *
 * ⚠️ The exemption rule is shared, not re-derived here — `lib/laborHours`, which
 * mirrors the API's `common/labor-hours`. Complete/Cancelled jobs and SYSTAC bid
 * placeholders are exempt: nagging for hours nobody will ever enter trains people
 * to ignore the flag. The API also returns `estimatedHours.state`; prefer it when
 * you have it, and it is what this reads.
 */
function EstimatedHoursPrompt({ project: p, onOpenTab }: { project: ProjectDetail; onOpenTab: (key: string) => void }) {
  const eh = p.estimatedHours;
  // Fall back to the shared client rule if an older payload lacks `state`.
  const state = eh?.state ?? laborHoursState(eh?.effective ?? null, p.projectStatus?.label, p.jobNumber, p.bidClosedWonAt);
  if (state !== 'missing') return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 9, flexWrap: 'wrap',
      background: '#FFFDF5', border: '1px solid #E7D9A6', borderRadius: 'var(--ew-r-md)',
      padding: '9px 12px', marginBottom: 'var(--ew-s-4)', fontSize: 12.5, lineHeight: 1.5,
      color: '#6B5D1F',
    }}>
      <span style={{ fontWeight: 700 }}>Estimated labor hours missing</span>
      <span>
        Nobody has entered the estimated labor hours for this job, and no labor estimate has
        synced from Intacct to derive them from (jobs with one pre-load automatically). The{' '}
        <b>estimator</b> sets these at job set-up — without them there is no target to
        measure actual hours against.{' '}
        <button
          type="button"
          className="ew-btn-link"
          style={{ color: 'var(--ew-quality)', font: 'inherit', fontWeight: 600 }}
          onClick={() => onOpenTab('manpower')}
        >
          Enter them on the Manpower tab →
        </button>
      </span>
    </div>
  );
}

/**
 * "No contract in Intacct for this job."
 *
 * Product owner, 2026-08-04: the contract comes from the Intacct API sync, and when it is
 * absent that is an ESTIMATOR task — set the contract up in Intacct, not type a
 * number here. Nothing in the tracker can fix it.
 *
 * ⚠️ Never shown to field leaders. Their contract fields are redacted, so the
 * figure is null for them regardless and a naive check would flag every job —
 * see lib/contractSource.
 */
function ContractSourcePrompt({ project: p }: { project: ProjectDetail }) {
  /**
   * ⚠️ Reads the SERVER-computed state. It was computed client-side from
   * "can this viewer see contracts?", which was right in the steady state and
   * wrong while loading: isFieldLeaderOnly(undefined) is deliberately false, so
   * on a cold cache a field leader briefly counted as office — and their
   * contract is already redacted to null, so every job flashed this prompt
   * (review, #297). The server knows the viewer before it answers.
   */
  if (p.contractState !== "missing") return null;

  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 9, flexWrap: "wrap",
      background: "#FFFDF5", border: "1px solid #E7D9A6", borderRadius: "var(--ew-r-md)",
      padding: "9px 12px", marginBottom: "var(--ew-s-4)", fontSize: 12.5, lineHeight: 1.5,
      color: "#6B5D1F",
    }}>
      <span style={{ fontWeight: 700 }}>No contract in Intacct</span>
      <span>
        Sage Intacct has no contract for this job, so the tracker has no contract value to
        show. The <b>estimator</b> sets the contract up in Intacct — it syncs across
        overnight. There is nothing to enter here.
      </span>
    </div>
  );
}

function CrewScheduleChangeNotice({ projectId, onOpenTab }: { projectId: number; onOpenTab: (key: string) => void }) {
  const q = useQuery({
    queryKey: ['manpower', 'recent-changes', projectId],
    queryFn: () => manpowerApi.recentScheduleChanges(projectId, 3),
    enabled: Number.isFinite(projectId),
    staleTime: 60_000,
  });
  const events = q.data ?? [];
  if (!events.length) return null;

  const [latest, ...earlier] = events;
  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap',
        background: '#E7F0F8', border: '1px solid #CFE0EF', borderRadius: 'var(--ew-r-md)',
        padding: '9px 12px', marginBottom: 'var(--ew-s-4)', fontSize: 12.5, lineHeight: 1.5,
        color: '#15659E',
      }}
    >
      <span style={{ fontWeight: 700 }}>◇ Crew schedule updated</span>
      <span style={{ color: 'var(--ew-n-700)' }}>
        {fmtDateTime(latest.at)}
        {latest.by ? ` by ${latest.by}` : ''}
        {latest.detail ? ` — ${latest.detail}` : ''}
      </span>
      {earlier.length > 0 && (
        <span style={{ color: 'var(--ew-n-500)' }} title={earlier.map((e) => `${fmtDateTime(e.at)}${e.by ? ` by ${e.by}` : ''}${e.detail ? ` — ${e.detail}` : ''}`).join('\n')}>
          +{earlier.length} earlier change{earlier.length === 1 ? '' : 's'}
        </span>
      )}
      <button
        type="button"
        onClick={() => onOpenTab('manpower')}
        style={{
          marginLeft: 'auto', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          font: 'var(--ew-fw-semibold) 12.5px/1 var(--ew-font)', color: '#15659E', textDecoration: 'underline',
        }}
      >
        View schedule
      </button>
    </div>
  );
}

function Overview({ project: p, contractValue, hideContract, onOpenTab, editLocked }: { project: ProjectDetail; contractValue: number | null; hideContract: boolean; onOpenTab: (key: string, extra?: Record<string, string>) => void; editLocked: boolean }) {
  // SYSTAC bids are read-only until Closed-Won — the edit lock overrides the
  // permission grant, so every inline section, the schedule editor, and Notes
  // render read-only. The API's ProjectStageGuard enforces the same.
  const canEdit = useCanEditProject() && !editLocked;
  const qc = useQueryClient();
  const [editSchedule, setEditSchedule] = useState(false);
  const [acceptingMobEnd, setAcceptingMobEnd] = useState(false);
  // Set when accept re-read and found the divergence already gone (#379 B).
  const [mobEndResolved, setMobEndResolved] = useState(false);
  const rfiQuery = useQuery<any[]>({ queryKey: ['rfis', p.id], queryFn: () => rfisApi.list({ projectId: p.id }) as Promise<any[]>, staleTime: 60_000, retry: false });
  const safetyQuery = useQuery<{ available: boolean; goodCatch: { total: number }; inspections: { total: number }; equipmentInspections: { total: number } }>({ queryKey: ['project-safety', p.id], queryFn: () => projectsApi.safety(p.id) as Promise<any>, staleTime: 60_000, retry: false });
  // Actual hours (Paycor) — shares the Manpower tab's cache; drives the per-phase
  // "actual hours to date" readout on the Schedule & Dates card.
  const manpowerQuery = useQuery({ queryKey: ['project-manpower', p.id], queryFn: () => projectsApi.manpower(p.id), staleTime: 60_000, retry: false });
  // Users power the Team section's PM/engineer/estimator/safety selects (same
  // list the retired edit modal used). Only needed once an edit is opened, but
  // cheap + cached, so fetched alongside the rest.
  const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list(), staleTime: 300_000 });
  const openRfis = rfiQuery.data ? rfiQuery.data.filter((r) => r.status === 'open').length : null;

  // ── Per-phase schedule (Schedule & Dates card + Overall Completion) ──
  const fab = isFabDivision(p);
  const phaseKeys = phaseKeysFor(p);
  // Show a block for every expected phase, backfilling blanks for phases with no
  // row yet (so a fab job always shows both fab + install).
  const existingPhases = new Map((p.phases ?? []).map((ph) => [ph.phase, ph]));
  /**
   * Stored phase row wins; otherwise seed from the legacy project dates so the
   * card reflects an existing schedule (and the drawer pre-fills) with no re-entry.
   *
   * ⚠️ AN UNCLAIMED 'project' phase takes its actual start from Paycor here,
   * because the server-side derivation cannot always reach it. That derivation
   * UPDATEs `project_phases`, and most non-fabrication jobs have no row there —
   * one is created only when somebody opens the schedule drawer. Without this, a
   * job whose crew has been on site for weeks renders Not Started, which is the
   * mirror of the defect #380 exists to fix and hits far more jobs than the 13 it
   * corrects.
   *
   * ⚠️ "UNCLAIMED", not "unstored", and the difference matters. Saving any
   * unrelated schedule field on a phase-less job INSERTs a row with a blank actual
   * start, and keying this off `stored` alone would let that flip the card back to
   * Not Started until the nightly job ran — indefinitely while PAYCOR_SYNC_ENABLED
   * is off. So the condition is the same claimability rule the server uses: no
   * actual start, and nobody has spoken for it. A PM's override or explicit clear
   * stamps `actual_start_source = 'manual'` and correctly suppresses this.
   *
   * ⚠️ Only 'project', never a seeded fabrication or installation — Paycor's
   * MIN(work_date) is per JOB, so on a two-phase job it cannot say which of the
   * two began. Same restriction as the server derivation, for the same reason.
   *
   * ⚠️ The value is computed SERVER-side (`derivedActualStart`) by the same
   * definition the derivation uses. It must not be recomputed from the `daily`
   * actuals: those filter on job number alone, with no `is_field = 1` and no
   * exclusion list, so a job carrying only Training time — or an excluded
   * container — would report a start it should not have.
   *
   * ⚠️ DISPLAY ONLY. It never reaches a save: `mobEndDivergence` below is
   * deliberately given the RAW `seedPhaseFromLegacy`, because its result feeds a
   * write payload and persisting this would stamp a provenance nothing checked.
   */
  const derivedActualStart = manpowerQuery.data?.derivedActualStart ?? null;
  const displayPhases: ProjectPhase[] = phaseKeys.map((k) => {
    const base = existingPhases.get(k) ?? seedPhaseFromLegacy(k, p);
    const unclaimed = actualStartUnclaimed(base);
    return k === 'project' && unclaimed && derivedActualStart
      ? { ...base, actualStart: derivedActualStart, actualStartSource: 'paycor' as const }
      : base;
  });
  /**
   * Does the crew schedule agree with this card about when the job starts (#385)?
   *
   * ⚠️ Compared against `currentStartDate` — the PROJECT-level start — and not a
   * phase's `plannedStart`. The crew grid has one row per job and flags that same
   * job against that same column (`current_start_date`, which is MIN(plannedStart)
   * since #380); a flag that fired on one screen and not the other would be worse
   * than neither. On a single-phase job the two are the same date anyway.
   *
   * ⚠️ `scheduled: false` — a job with no crew booked at all — yields no flag,
   * which is right: there is nothing for the plan to disagree with, and the grid
   * already has a home for that case in its "⚠ Needs Scheduling" section.
   *
   * ⚠️ SHARES ManpowerTab's query key deliberately. Both read the same endpoint at
   * the same `weeks`, so one cache entry serves both: no second request where the
   * tab has already loaded, and — the reason that matters — ONE thing to
   * invalidate when published crew moves, rather than a second cache quietly
   * holding a stale first-crew date. See `invalidateStartAlignmentQueries`. Keep
   * the `weeks` argument in step if either caller changes it; the key does not
   * carry it.
   */
  const crewSummaryQuery = useQuery({
    queryKey: ['manpower', 'schedule-summary', p.id],
    queryFn: () => manpowerApi.projectScheduleSummary(p.id, 8),
    enabled: Number.isFinite(p.id),
    staleTime: 60_000,
    retry: false,
    /**
     * ⚠️ The mirror image of the poll on the crew grid, needed for the same
     * reason: invalidation is per-client, and THIS side's input — the first
     * booked crew day — is moved by the **Scheduler**, in a different session. A
     * PM sitting on this Overview cannot hear a publish, so without an interval
     * the banner can keep claiming a disagreement the Scheduler booked around an
     * hour ago.
     *
     * Five minutes, matching the grid and the two Today pages.
     * `refetchIntervalInBackground` stays at its default of false.
     */
    refetchInterval: 5 * 60 * 1000,
  });
  // ⚠️ Read off BOTH branches, not just `scheduled: true`. A job whose only crew
  // sits outside the summary's display window still has a first booked day, and
  // the grid — whose read is unbounded — would flag it. Gating on `scheduled`
  // made the card silent on exactly those jobs.
  //
  // ⚠️ AND BOTH INPUTS COME FROM THIS ONE QUERY, not one from here and the plan
  // from `p`. `['project', id]` does not poll and cannot refetch on focus, so
  // the planned start read off it would stay frozen while the crew date beside
  // it refreshed every five minutes — a fresh value held against a stale one,
  // which can assert a disagreement that was never true at any single moment.
  // Two inputs, one read, one snapshot. The crew grid has always had this
  // property, because both of its inputs arrive on one project-options row.
  //
  // ⚠️ AND NEVER ON A FINISHED JOB. The grid drops Complete and Canceled jobs,
  // so their start never flags there; a finished job's start is history, not
  // something to reconcile (#385, follow-up to #540). Before this the card kept
  // the banner on exactly those jobs — the two screens disagreeing. The status
  // it goes by comes from the crew summary too, for the same one-snapshot
  // reason; `p`'s label is only the fallback for an API that predates it.
  const startMismatchFlag = overviewStartMismatch(
    crewSummaryQuery.data,
    p.projectStatus?.label,
  );

  const completion = weightedOverall(displayPhases);
  const fabPhase = displayPhases.find((ph) => ph.phase === 'fabrication');
  const instPhase = displayPhases.find((ph) => ph.phase === 'installation');
  // The completion panel needs real progress (a % on file); dates alone (which
  // most projects now have via legacy seeding) shouldn't force a "0%" panel.
  const hasCompletion = displayPhases.some((ph) => ph.completionPct != null);

  // ── Mob-end vs. plan divergence ──
  // Computed by the shared `mobEndDivergence` so the render and the accept
  // action cannot disagree — see the note on that function.
  const mobEndFlag = mobEndDivergence({
    phases: p.phases,
    mobilizations: p.mobilizations,
    phaseKeys,
    seed: (k) => seedPhaseFromLegacy(k, p),
  });

  const acceptMobEnd = async () => {
    if (!mobEndFlag) return;
    setAcceptingMobEnd(true);
    setMobEndResolved(false);
    try {
      // Re-read at CLICK time, not page-load time. Both #379 bugs come from
      // writing a snapshot that may be minutes old.
      //
      // ⚠️ `staleTime: 0` is load-bearing. queryClient sets a GLOBAL 2-minute
      // staleTime and fetchQuery honours it, so without this override the
      // "fresh" read is served from cache and the fix does nothing at all.
      const fresh = await qc.fetchQuery({
        queryKey: ['project', p.id],
        queryFn: () => projectsApi.get(p.id) as Promise<ProjectDetail>,
        staleTime: 0,
      });
      // Re-READING is not enough — the decision has to be re-MADE. Recompute the
      // divergence from that same response, and no-op when it has gone: if
      // another user already pushed the phase end past the mobilization, the
      // flag on this page is stale and accepting would roll their newer end
      // BACKWARD (#379 B).
      const live = mobEndDivergence({
        phases: fresh.phases,
        mobilizations: fresh.mobilizations,
        phaseKeys: phaseKeysFor(fresh),
        seed: (k) => seedPhaseFromLegacy(k, fresh),
      });
      // Two ways not to write, and they are one rule: only ever perform the
      // action the user actually approved.
      //   • `live` is null — someone already resolved it (#379 B).
      //   • `live` exists but names a different phase or date — the divergence
      //     MOVED, so this is a different action and nobody approved it. Without
      //     this the button could say "use Sep 2 as Installation end" and
      //     quietly update Fabrication, or persist a date never displayed.
      // Either way write nothing, refresh, and SAY SO — a silently vanishing
      // alert is indistinguishable from a broken button.
      if (!sameDivergenceAction(mobEndFlag, live)) {
        setMobEndResolved(true);
        qc.invalidateQueries({ queryKey: ['project', p.id] });
        return;
      }

      // ONE payload, whichever branch the server takes (#383). This used to
      // fork on stored-vs-seeded, because an INSERT writes only the columns it
      // is given and a sparse one left a row holding `actual_end` alone — the
      // card blanked and job 10634's pre-mob original end was gone. So the
      // seeded branch had to send the whole displayed date set, built from a
      // page-load snapshot, which is bug #379 A in the other direction.
      //
      // The server now seeds the columns this does not mention from the
      // project's own legacy dates (PHASE_LEGACY_SEED), reading them at write
      // time. The row it creates is identical to the one the full-set payload
      // used to send, minus the staleness — so the client no longer has to
      // predict the branch, and this sends only what accepting actually means.
      //
      // ⚠️ Still no `actualStart` key, and it must stay absent even though this
      // is now a partial patch in both directions. The server reads the KEY's
      // presence as a human speaking and stamps `actual_start_source = 'manual'`,
      // which opts the job out of the Paycor derivation for good — as a side
      // effect of accepting a mobilization END date.
      const patch = {
        phase: live.phase,
        actualEnd: live.mobEnd,
        isProjectedEnd: true,
        // Opt into the server's optimistic-concurrency guard. Re-reading above
        // shrank the race to one round trip; this closes it. Without it, an edit
        // landing between that read and this write is silently rolled back — the
        // very failure this action is being fixed for.
        //
        // Only meaningful for a phase that HAS a row: a legacy-seeded one has
        // nothing to conflict with and no timestamp to send, and the server
        // ignores the token on the INSERT path anyway.
        expectedUpdatedAt: live.stored ? live.row.updatedAt ?? undefined : undefined,
      };
      // upsertPhases back-fills the legacy project dates, so
      // header/portfolio/health follow.
      await projectsApi.updatePhases(p.id, [patch]);
      // ⚠️ "health follow" was not true of the TODAY pages, which key under
      // `['today']` and were not invalidated here — this accepts a new
      // mobilization end date, one of `deriveHealth`'s own inputs.
      // (Codex, P2 on #513.)
      await invalidateHealthQueries(qc, p.id);
      // ⚠️ upsertPhases back-fills current_start_date through
      // syncLegacyScheduleDates, which writes it UNCONDITIONALLY from
      // MIN(plannedStart) -- so seeding a phase row here can move the plan half of
      // the start-mismatch flag even though this save is about an END date (#385).
      await invalidateStartAlignmentQueries(qc, p.id);
      qc.invalidateQueries({ queryKey: ['kpi'] });
    } catch (err: unknown) {
      // 409 = the row changed inside our round trip and the server refused to
      // overwrite it. Same outcome for the user as the no-op above — nothing was
      // clobbered — so it gets the same notice. Anything else is a real error and
      // must keep propagating rather than being reported as a happy path.
      //
      // ⚠️ Must go through isConflictError, NOT `err.response.status`: the
      // apiClient interceptor replaces the Axios error, so `.response` is gone
      // by the time it reaches here and a direct check silently never matches.
      if (isConflictError(err)) {
        setMobEndResolved(true);
        qc.invalidateQueries({ queryKey: ['project', p.id] });
      } else {
        throw err;
      }
    } finally {
      setAcceptingMobEnd(false);
    }
  };

  // Actual hours by phase. A single-phase (non-fab) job needs no split — all job
  // hours are that one phase's, so treat it as mapped. A fab job needs the
  // cost_code split; if none is mapped, fall back to whole-job on each block
  // with a note.
  const byPhaseArr = manpowerQuery.data?.byPhase ?? [];
  const whole = manpowerQuery.data?.summary?.totalHours ?? null;
  const actualsByPhase = new Map<string, number>();
  let actualsMapped: boolean;
  if (phaseKeys.length === 1) {
    actualsMapped = true;
    if (whole != null) actualsByPhase.set(phaseKeys[0], whole);
  } else {
    actualsMapped = byPhaseArr.some((e) => e.phase !== 'unmapped');
    if (actualsMapped) {
      for (const e of byPhaseArr) if (e.phase !== 'unmapped') actualsByPhase.set(e.phase, e.hours);
    } else if (whole != null) {
      for (const k of phaseKeys) actualsByPhase.set(k, whole);
    }
  }

  // Health mirrors the single server-derived (schedule) value everywhere — no
  // labor adjustment here (that would diverge from Portfolio/header). The labor
  // over-run is surfaced as its OWN signal on the card (alert + per-phase chip);
  // folding labor into the shared server health is a separate task.
  //
  // Rendered by HealthExplainer below, and by the badge on the workspace header.
  // It deliberately has NO KPI card: with the header badge and the explainer, a
  // third copy of the same word put "At Risk" on screen three times over
  // (product owner, 2026-07-28).
  const healthRule = (p.healthRule ?? null) as HealthRule | null;
  const totalBudget = displayPhases.reduce((s, ph) => s + (ph.budgetLaborHours ?? 0), 0) || null;
  const overallLabor = laborStatus(manpowerQuery.data?.summary?.totalHours ?? null, totalBudget, completion.overallPct);

  // ⚠️ `hideContract` is NOT the same as a null contract. An office user whose
  // job has no contract entered should still see "Contract —" (it tells them
  // it's unset); a field leader must not see the tile at all. The API has
  // already stripped the figure — this only keeps the card from advertising it.
  const kpis: { label: string; value: string; strip: string; sub?: string }[] = (fab
    ? [
        { label: 'Contract',      value: money(contractValue),                             strip: 'var(--ew-green)' },
        { label: 'Fabrication',   value: `${fabPhase?.completionPct ?? 0}% complete`,      strip: 'var(--ew-dark)' },
        { label: 'Installation',  value: PHASE_STATUS_LABEL[phaseStatus(instPhase ?? { completionPct: null, actualStart: null })], strip: 'var(--ew-quality)' },
        { label: 'Target compl.', value: fmtDate(instPhase?.actualEnd ?? p.effectiveCompletion ?? p.estimatedCompletion), strip: 'var(--ew-n-300)' },
      ]
    : [
        { label: 'Contract',      value: money(contractValue),               strip: 'var(--ew-green)' },
        { label: 'Status',        value: p.projectStatus?.label ?? '—',      strip: 'var(--ew-quality)' },
        { label: 'Mobilization',  value: p.mobilizationStatus?.label ?? '—', strip: 'var(--ew-performance)' },
        { label: 'Target compl.', value: fmtDate(p.effectiveCompletion ?? p.estimatedCompletion),     strip: 'var(--ew-n-300)' },
      ]
  ).filter((k) => !(hideContract && k.label === 'Contract'));

  return (
    <>
      <OverviewEditStyles />
      <CrewScheduleChangeNotice projectId={p.id} onOpenTab={onOpenTab} />
      <HealthExplainer health={p.health} rule={healthRule} reason={p.healthReason ?? null} />
      <MobilizationPrompt project={p} />
      <EstimatedHoursPrompt project={p} onOpenTab={onOpenTab} />
      <ContractSourcePrompt project={p} />
      {hasCompletion ? (
        <div style={{ marginBottom: 'var(--ew-s-4)' }}>
          <OverallCompletionPanel completion={completion} phases={displayPhases} />
        </div>
      ) : null}
      <div style={{ display: 'grid', gap: 'var(--ew-s-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {kpis.map((k) => (
          <div key={k.label} className="pw-kpi">
            <span className="pw-kpi-strip" style={{ background: k.strip }} />
            <span className="pw-kpi-label">{k.label}</span>
            <span className="pw-kpi-value">{k.value}</span>
            {k.sub ? <span style={{ font: 'var(--ew-fw-medium) 11px/1.3 var(--ew-font)', color: 'var(--ew-performance)' }}>{k.sub}</span> : null}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 'var(--ew-s-4)' }}>
        <PhaseScheduleCard
          phases={displayPhases}
          actualsByPhase={actualsByPhase}
          actualsMapped={actualsMapped}
          fab={fab}
          canEdit={canEdit}
          onEdit={() => setEditSchedule(true)}
          laborAlert={overallLabor}
          mobEndFlag={mobEndFlag}
          onAcceptMobEnd={acceptMobEnd}
          acceptingMobEnd={acceptingMobEnd}
          mobEndResolved={mobEndResolved}
          onDismissMobEndResolved={() => setMobEndResolved(false)}
          startMismatch={startMismatchFlag}
        />
      </div>

      {editSchedule ? (
        <PhaseScheduleDrawer
          projectId={p.id}
          phaseKeys={phaseKeys as PhaseKey[]}
          phases={displayPhases}
          onClose={() => setEditSchedule(false)}
        />
      ) : null}

      <div className="pw-two-col" style={{ marginTop: 'var(--ew-s-4)' }}>
        {/* Left — inline-editable project facts (mirrors the retired edit modal) */}
        <div style={{ display: 'grid', gap: 'var(--ew-s-4)' }}>
          <BasicsSection project={p} canEdit={canEdit} />
          <TeamSection project={p} canEdit={canEdit} users={users} />
          <ScheduleSection project={p} canEdit={canEdit} />
          <NoticeToProceedSection project={p} />
          <JobInfoSection project={p} canEdit={canEdit} />
          <BidLinkSection project={p} canEdit={canEdit} />
        </div>

        {/* Right — live RFI + Safety summaries, then the activity feed */}
        <div style={{ display: 'grid', gap: 'var(--ew-s-4)' }}>
          <div className="pw-card">
            <h2 className="pw-card-hd">Open RFIs
              <a onClick={() => onOpenTab('rfis')} style={{ marginLeft: 'auto', font: 'var(--ew-fw-medium) 12px var(--ew-font)', color: 'var(--ew-green)', cursor: 'pointer', textTransform: 'none', letterSpacing: 0 }}>All →</a>
            </h2>
            <div style={{ padding: 'var(--ew-s-4)', display: 'flex', alignItems: 'baseline', gap: 'var(--ew-s-2)' }}>
              <span style={{ font: 'var(--ew-fw-semibold) 28px/1 var(--ew-font)', fontVariantNumeric: 'tabular-nums' }}>{rfiQuery.isError ? '—' : (openRfis ?? '…')}</span>
              <span style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', color: 'var(--ew-n-500)' }}>open</span>
            </div>
          </div>
          <div className="pw-card">
            <h2 className="pw-card-hd">Safety
              <a onClick={() => onOpenTab('safety')} style={{ marginLeft: 'auto', font: 'var(--ew-fw-medium) 12px var(--ew-font)', color: 'var(--ew-quality)', cursor: 'pointer', textTransform: 'none', letterSpacing: 0 }}>All →</a>
            </h2>
            <div style={{ padding: 'var(--ew-s-4)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', color: 'var(--ew-n-700)' }}>
              {safetyQuery.isError || !safetyQuery.data?.available
                ? <span style={{ color: 'var(--ew-n-400)' }}>Not available</span>
                : <span><b>{safetyQuery.data.goodCatch.total}</b> Good Catches · <b>{safetyQuery.data.inspections.total}</b> site · <b>{safetyQuery.data.equipmentInspections.total}</b> equip insp.</span>}
            </div>
          </div>
          {/* Who did what in this job. In the sidebar rather than below Notes so
              it's visible without scrolling — the two cards above it are only a
              few lines each, so the feed still starts above the fold. Read-only
              for everyone who can see the job; the API filters cost-bearing
              events for users without financials access. */}
          <ProjectActivityCard projectId={p.id} onOpenTab={onOpenTab} />
        </div>
      </div>

      {/* Notes (full width) — add / edit-own / delete, same gating as the modal */}
      <div style={{ marginTop: 'var(--ew-s-4)' }}>
        <NotesSection project={p} canEdit={canEdit} />
      </div>

      {/* Danger Zone (bottom) — shown only with the projects.delete permission */}
      <div style={{ marginTop: 'var(--ew-s-4)' }}>
        <DangerZoneSection project={p} />
      </div>
    </>
  );
}
